import { useState, useMemo, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import type { SpecListItem } from '../../services/specificationService';
import SingleSelectDropdown from '../common/SingleSelectDropdown';

// ============================================================================
// SpecSelectModal — TC Import 시 기능명세 선택 모달 (CHG-030 / REQ-V30-002~006)
// ============================================================================

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  IN_REVIEW: 'In Review',
  APPROVED: 'Approved',
  IMPLEMENTED: 'Implemented',
};

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  IN_REVIEW: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  IMPLEMENTED: 'bg-blue-100 text-blue-800',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold ${STATUS_COLOR[status] || 'bg-gray-100 text-gray-700'}`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <span className="bg-yellow-100">{text.slice(index, index + query.length)}</span>
      {text.slice(index + query.length)}
    </>
  );
}

interface SpecSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (specId: string) => void;
  projectId: string;
  specs: SpecListItem[];
}

export default function SpecSelectModal({
  isOpen,
  onClose,
  onSelect,
  projectId: _projectId,
  specs,
}: SpecSelectModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [requirementFilter, setRequirementFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedSpecId, setSelectedSpecId] = useState<string | null>(null);

  // 모달 열릴 때 state 초기화
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setRequirementFilter('all');
      setStatusFilter('all');
      setSelectedSpecId(null);
    }
  }, [isOpen]);

  // 고유 요구사항 목록 추출 (필터 옵션 생성)
  const uniqueRequirements = useMemo(() => {
    const reqMap = new Map<string, { requirementId: string; requirementKey: string; title: string }>();
    for (const spec of specs) {
      if (spec.requirement && !reqMap.has(spec.requirement.id)) {
        reqMap.set(spec.requirement.id, {
          requirementId: spec.requirement.id,
          requirementKey: spec.requirement.reqId,
          title: spec.requirement.title,
        });
      }
    }
    return Array.from(reqMap.values());
  }, [specs]);

  // debounce 검색
  const debouncedQuery = useDebounce(searchQuery, 200);

  // 필터링
  const filteredSpecs = useMemo(() => {
    return specs.filter((spec) => {
      // 검색어 필터
      if (debouncedQuery) {
        const q = debouncedQuery.toLowerCase();
        const matchCode = spec.functionCode.toLowerCase().includes(q);
        const matchTitle = spec.title.toLowerCase().includes(q);
        if (!matchCode && !matchTitle) return false;
      }

      // 요구사항 필터
      if (requirementFilter !== '') {
        if (spec.requirementId !== requirementFilter) return false;
      }

      // 상태 필터
      if (statusFilter !== '') {
        if (spec.status !== statusFilter) return false;
      }

      return true;
    });
  }, [specs, debouncedQuery, requirementFilter, statusFilter]);

  if (!isOpen) return null;

  return (
    <>
      {/* 오버레이 */}
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />

      {/* 모달 */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl w-[640px] max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">기능명세 선택</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 검색 + 필터 영역 */}
          <div className="px-6 py-3 space-y-3 border-b border-gray-100">
            {/* 검색 */}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="기능코드 또는 제목으로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            {/* 필터 */}
            <div className="flex items-center gap-3">
              {/* 연결 요구사항 필터 */}
              <SingleSelectDropdown
                label="연결 요구사항: 전체"
                value={requirementFilter}
                onChange={setRequirementFilter}
                options={uniqueRequirements.map((req) => ({
                  value: req.requirementId,
                  label: `${req.requirementKey} ${req.title}`,
                }))}
              />

              {/* 상태 필터 */}
              <SingleSelectDropdown
                label="상태: 전체"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'IN_REVIEW', label: 'In Review' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'IMPLEMENTED', label: 'Implemented' },
                ]}
              />
            </div>
          </div>

          {/* 테이블 영역 */}
          <div className="flex-1 overflow-y-auto max-h-[400px]">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="text-left text-sm text-gray-500 uppercase">
                  <th className="px-6 py-2 w-[120px]">기능코드</th>
                  <th className="px-4 py-2">제목</th>
                  <th className="px-4 py-2 w-[160px]">연결 요구사항</th>
                  <th className="px-4 py-2 w-[80px]">상태</th>
                </tr>
              </thead>
              <tbody>
                {filteredSpecs.length > 0 ? (
                  filteredSpecs.map((spec) => (
                    <tr
                      key={spec.id}
                      className={`cursor-pointer border-b border-gray-50 transition-colors
                        ${
                          selectedSpecId === spec.id
                            ? 'bg-blue-50 border-l-2 border-l-blue-500'
                            : 'hover:bg-gray-50'
                        }`}
                      onClick={() => setSelectedSpecId(spec.id)}
                      onDoubleClick={() => {
                        setSelectedSpecId(spec.id);
                        onSelect(spec.id);
                        onClose();
                      }}
                    >
                      <td className="px-6 py-2.5 text-sm font-mono text-gray-700">
                        {highlightMatch(spec.functionCode, debouncedQuery)}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-900 truncate max-w-0">
                        {highlightMatch(spec.title, debouncedQuery)}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-500">
                        {spec.requirement?.reqId || '-'}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={spec.status} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">
                      검색 결과가 없습니다
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 푸터 */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <span className="text-sm text-gray-500">
              {filteredSpecs.length === specs.length
                ? `${specs.length}건 조회`
                : `${filteredSpecs.length}건 / ${specs.length}건`}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                onClick={onClose}
              >
                취소
              </button>
              <button
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={!selectedSpecId}
                onClick={() => {
                  if (selectedSpecId) {
                    onSelect(selectedSpecId);
                    onClose();
                  }
                }}
              >
                선택
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
